import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import type { TablesInsert, TablesUpdate } from '../../../supabase/functions/_shared/database.types'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import type { CalendarEntry } from '../types/database.tables'
import { formatDateForComparison } from '../utils/dateUtils'

export type CalendarEntryCreate = TablesInsert<'calendar_entries'>
export type CalendarEntryUpdate = TablesUpdate<'calendar_entries'>

export interface UseCalendarEntriesReturn {
  entries: CalendarEntry[]
  isLoading: boolean
  error: string | null
  loadEntries: (startDate: Date, endDate: Date) => Promise<void>
  createEntry: (data: CalendarEntryCreate) => Promise<CalendarEntry>
  updateEntry: (id: string, data: CalendarEntryUpdate) => Promise<CalendarEntry>
  deleteEntry: (id: string) => Promise<void>
  getEntriesForDate: (date: Date) => CalendarEntry[]
}

const buildQueryKey = (userId: string | undefined, startDate?: Date, endDate?: Date) => [
  'calendar-entries',
  userId,
  startDate?.toISOString(),
  endDate?.toISOString(),
]

export const useCalendarEntries = (): UseCalendarEntriesReturn => {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [currentRange, setCurrentRange] = useState<{ start: Date; end: Date } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const queryKey = useMemo(
    () => buildQueryKey(user?.id, currentRange?.start, currentRange?.end),
    [user?.id, currentRange?.start, currentRange?.end]
  )

  const loadEntries = useCallback(
    async (startDate: Date, endDate: Date): Promise<void> => {
      const nextQueryKey = buildQueryKey(user?.id, startDate, endDate)
      setCurrentRange({ start: startDate, end: endDate })
      if (!user) {
        queryClient.setQueryData(nextQueryKey, [])
        return
      }

      const startDateString = formatDateForComparison(startDate)
      const endDateString = formatDateForComparison(endDate)

      try {
        await queryClient.fetchQuery({
          queryKey: nextQueryKey,
          queryFn: async () => {
            const { data, error: dbError } = await supabase
              .from('calendar_entries')
              .select('*')
              .eq('user_id', user.id)
              .gte('date', startDateString)
              .lte('date', endDateString)
              .order('date', { ascending: true })
              .order('start_time', { ascending: true })

            if (dbError) {
              throw dbError
            }

            return data || []
          },
        })
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load calendar entries'
        setError(errorMessage)
        queryClient.setQueryData(nextQueryKey, [])
      }
    },
    [queryClient, user]
  )

  const entriesQuery = useQuery<CalendarEntry[]>({
    queryKey,
    enabled: Boolean(user?.id && currentRange),
    initialData: [],
    queryFn: async () => {
      if (!currentRange || !user) return []
      const { start, end } = currentRange
      const { data, error: dbError } = await supabase
        .from('calendar_entries')
        .select('*')
        .eq('user_id', user.id)
        .gte('date', formatDateForComparison(start))
        .lte('date', formatDateForComparison(end))
        .order('date', { ascending: true })
        .order('start_time', { ascending: true })

      if (dbError) {
        throw dbError
      }

      return data || []
    },
  })

  const createEntry = useCallback(
    async (data: CalendarEntryCreate): Promise<CalendarEntry> => {
      if (!user) {
        throw new Error('User not authenticated')
      }

      const newEntry = {
        ...data,
        user_id: user.id,
      } as CalendarEntry

      const { data: createdData, error: dbError } = await supabase
        .from('calendar_entries')
        .insert(newEntry)
        .select()
        .single()

      if (dbError) {
        throw dbError
      }

      if (currentRange) {
        queryClient.setQueryData<CalendarEntry[]>(queryKey, (prev = []) => [createdData, ...prev])
      }

      return createdData
    },
    [currentRange, queryClient, queryKey, user]
  )

  // Function to update existing calendar entry
  const updateEntry = useCallback(
    async (id: string, updates: CalendarEntryUpdate): Promise<CalendarEntry> => {
      if (!user) {
        throw new Error('User not authenticated')
      }

      const { data, error: dbError } = await supabase
        .from('calendar_entries')
        .update(updates)
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single()

      if (dbError) {
        throw dbError
      }

      if (currentRange) {
        queryClient.setQueryData<CalendarEntry[]>(queryKey, (prev = []) =>
          prev.map((entry) => (entry.id === id ? data : entry))
        )
      }

      return data
    },
    [currentRange, queryClient, queryKey, user]
  )

  // Function to delete calendar entry
  const deleteEntry = useCallback(
    async (id: string): Promise<void> => {
      if (!user) {
        throw new Error('User not authenticated')
      }

      const { error: dbError } = await supabase.from('calendar_entries').delete().eq('id', id).eq('user_id', user.id)

      if (dbError) {
        throw dbError
      }

      if (currentRange) {
        queryClient.setQueryData<CalendarEntry[]>(queryKey, (prev = []) => prev.filter((entry) => entry.id !== id))
      }
    },
    [currentRange, queryClient, queryKey, user]
  )

  // Function to get entries for a specific date
  const getEntriesForDate = useCallback(
    (date: Date): CalendarEntry[] => {
      const dateString = formatDateForComparison(date)
      const cachedEntries = queryClient.getQueryData<CalendarEntry[]>(queryKey) || entriesQuery.data || []
      return cachedEntries.filter((entry) => entry.date === dateString)
    },
    [queryClient, queryKey, entriesQuery.data]
  )

  return {
    entries: entriesQuery.data || [],
    isLoading: entriesQuery.isLoading || entriesQuery.isFetching,
    error,
    loadEntries,
    createEntry,
    updateEntry,
    deleteEntry,
    getEntriesForDate,
  }
}
